import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface Testimonial {
    id: number;
    name: string;
    role: string;
    company: string;
    image: string;
    content: string;
    rating: number;
}

interface TestimonialFormData {
    name: string;
    role: string;
    company: string;
    image: File | null;
    content: string;
    rating: number;
}

const initialFormData: TestimonialFormData = {
    name: '',
    role: '',
    company: '',
    image: null,
    content: '',
    rating: 5
};

const testimonials: Testimonial[] = [
    {
        id: 1,
        name: 'Marie Joseph',
        role: 'CEO',
        company: 'TechHaiti',
        image: '/testimonials/marie.jpg',
        content: 'KolaboTech a transformé notre présence en ligne avec une solution innovante et performante. Leur expertise technique et leur professionnalisme sont remarquables.',
        rating: 5,
    },
    {
        id: 2,
        name: 'Pierre Louis',
        role: 'CTO',
        company: 'FinTech Solutions',
        image: '/testimonials/pierre.jpg',
        content: 'Une équipe exceptionnelle qui a su comprendre nos besoins et livrer une solution sur mesure. Leur support technique est incroyable.',
        rating: 5,
    },
    {
        id: 3,
        name: 'Sophie Martin',
        role: 'Directrice Marketing',
        company: 'Digital Haiti',
        image: '/testimonials/sophie.jpg',
        content: 'La qualité de leur travail et leur engagement envers l\'excellence ont dépassé nos attentes. Un partenaire technologique de confiance.',
        rating: 5,
    },
    {
        id: 4,
        name: 'Jean-Michel Baptiste',
        role: 'Directeur Général',
        company: 'Hôtel Le Plaza',
        image: '/testimonials/jean-michel.jpg',
        content: 'L\'installation du système de surveillance et la mise en place de notre infrastructure réseau ont été impeccables. Un service clientèle exemplaire.',
        rating: 5,
    },
    {
        id: 5,
        name: 'Anne-Marie Dubois',
        role: 'Propriétaire',
        company: 'Boutique en Ligne Kreol',
        image: '/testimonials/anne-marie.jpg',
        content: 'Grâce à KolaboTech, notre boutique en ligne est maintenant sécurisée et performante. Les ventes ont augmenté de 150% depuis le lancement.',
        rating: 5,
    },
    {
        id: 6,
        name: 'Marc Antoine',
        role: 'Responsable IT',
        company: 'Banque Nationale',
        image: '/testimonials/marc.jpg',
        content: 'La migration de nos services vers le cloud s\'est déroulée sans accroc. L\'expertise de KolaboTech en matière de sécurité est impressionnante.',
        rating: 5,
    },
];

const TestimonialCard: React.FC<{ testimonial: Testimonial }> = ({ testimonial }) => {
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 md:p-8 mx-2 w-full h-full flex flex-col"
        >
            <div className="flex flex-col sm:flex-row items-start sm:items-center mb-6">
                <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 md:w-24 md:h-24 rounded-full object-cover mb-4 sm:mb-0 sm:mr-6"
                />
                <div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1">
                        {testimonial.name}
                    </h3>
                    <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 mb-1">
                        {testimonial.role}
                    </p>
                    <p className="text-sm md:text-md font-medium text-blue-600 dark:text-blue-400">
                        {testimonial.company}
                    </p>
                </div>
            </div>
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-300 mb-6 leading-relaxed flex-grow">
                {testimonial.content}
            </p>
            <div className="flex items-center mt-auto">
                {Array.from({ length: testimonial.rating }).map((_, index) => (
                    <svg
                        key={index}
                        className="w-4 h-4 md:w-5 md:h-5 text-yellow-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                ))}
            </div>
        </motion.div>
    );
};

const TestimonialSection = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState<TestimonialFormData>(initialFormData);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFormData({ ...formData, image: file });
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // TODO: Implement API call to save testimonial
        console.log('Testimonial submitted:', formData);
        // Reset form and close modal
        setFormData(initialFormData);
        setImagePreview('');
        setIsModalOpen(false);
    };

    const handleRatingChange = (newRating: number) => {
        setFormData({ ...formData, rating: newRating });
    };

    const totalPairs = Math.ceil(testimonials.length / 2);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentIndex((prevIndex) =>
                prevIndex === totalPairs - 1 ? 0 : prevIndex + 1
            );
        }, 5000);

        return () => clearInterval(timer);
    }, [totalPairs]);

    const getCurrentPair = () => {
        const startIndex = currentIndex * 2;
        return testimonials.slice(startIndex, startIndex + 2);
    };

    return (
        <section className="py-16 bg-gray-50 dark:bg-gray-900">
            <div className="container mx-auto px-4">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12"
                >
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                        Ce que disent nos clients
                    </h2>

                    <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                        Découvrez les témoignages de nos clients satisfaits et leurs expériences avec nos solutions
                    </p>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="mt-8 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                        onClick={() => setIsModalOpen(true)}
                    >
                        <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                        </svg>
                        Laisser un témoignage
                    </motion.button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="relative"
                >
                    <div className="relative overflow-hidden">
                        <div
                            className="flex transition-transform duration-500 ease-in-out"
                            style={{
                                transform: `translateX(-${currentIndex * 100}%)`,
                            }}
                        >
                            {Array.from({ length: totalPairs }).map((_, pairIndex) => (
                                <div key={pairIndex} className="w-full flex-shrink-0 flex flex-col md:flex-row justify-center gap-6 md:gap-12 px-4">
                                    {testimonials.slice(pairIndex * 2, pairIndex * 2 + 2).map((testimonial) => (
                                        <div key={testimonial.id} className="w-full md:max-w-[650px]">
                                            <TestimonialCard testimonial={testimonial} />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>

                        {/* Navigation arrows */}
                        <div className="absolute inset-y-0 left-0 md:flex items-center justify-center hidden">
                            <button
                                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                                className="p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                                aria-label="Previous testimonials"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                        </div>
                        <div className="absolute inset-y-0 right-0 md:flex items-center justify-center hidden">
                            <button
                                onClick={() => setCurrentIndex(Math.min(totalPairs - 1, currentIndex + 1))}
                                className="p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                                aria-label="Next testimonials"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>

                        {/* Mobile swipe indicators */}
                        <div className="flex justify-center mt-6 space-x-2">
                            {Array.from({ length: totalPairs }).map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${currentIndex === index
                                            ? 'bg-blue-600'
                                            : 'bg-gray-300 dark:bg-gray-600'
                                        }`}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>

                </motion.div>
            </div>

            {/* Testimonial Submission Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
                        onClick={() => setIsModalOpen(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                                    Partagez votre expérience
                                </h3>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Nom complet *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Rôle / Poste *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.role}
                                            onChange={e => setFormData({ ...formData, role: e.target.value })}
                                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Entreprise *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.company}
                                            onChange={e => setFormData({ ...formData, company: e.target.value })}
                                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Photo de profil
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="w-full text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-gray-700 dark:file:text-gray-300"
                                        />
                                        {imagePreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Votre témoignage *
                                    </label>
                                    <textarea
                                        required
                                        value={formData.content}
                                        onChange={e => setFormData({ ...formData, content: e.target.value })}
                                        rows={4}
                                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base resize-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Note
                                    </label>
                                    <div className="flex items-center space-x-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                type="button"
                                                onClick={() => handleRatingChange(star)}
                                                className="focus:outline-none p-1 sm:p-2"
                                            >
                                                <svg
                                                    className={`w-6 h-6 sm:w-8 sm:h-8 ${star <= formData.rating
                                                            ? 'text-yellow-400'
                                                            : 'text-gray-300 dark:text-gray-600'
                                                        }`}
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 sm:space-x-4 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="w-full sm:w-auto px-4 sm:px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-base font-medium"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-base font-medium"
                                    >
                                        Envoyer
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default TestimonialSection;